import CircleIcon from '@mui/icons-material/Circle';
import ErrorIcon from '@mui/icons-material/Error';
import MapsHomeWorkOutlinedIcon from '@mui/icons-material/MapsHomeWorkOutlined';
import { Box, Skeleton, Stack, Typography } from '@mui/material';
import { useIntl } from 'react-intl';

import { Icon } from '@verantos-dev/shared-components';
import { theme } from '@verantos-dev/shared-styles';
import { IsValidCreateUploadResponse } from '@verantos-dev/types-concepts';

import { useValidationResultsCount } from '../hooks/useValidationResultsCount';

const iconSx = { mr: 1, height: '16px', width: '16px' };

interface InfoItemProps {
  icon: JSX.Element;
  label: string;
  value: string | number;
}

const InfoItem = ({ icon, label, value }: InfoItemProps) => {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      {icon}

      <Typography color={theme.palette.text.secondary} variant="body2" marginRight={1}>
        {label}
      </Typography>
      <Typography color={theme.palette.text.primary} variant="body2">
        {value}
      </Typography>
    </Box>
  );
};

interface ValidationResultsHeaderProps {
  concepts?: IsValidCreateUploadResponse[];
  exporting?: boolean;
  onExportAll?: () => void;
  /** Total count from preview summary (overrides local computation) */
  totalCount?: number;
  /** Domain breakdown from preview summary (overrides local computation) */
  domainBreakdown?: Record<string, number>;
}

export default function ValidationResultsHeader({
  concepts,
  exporting,
  onExportAll,
  totalCount,
  domainBreakdown,
}: ValidationResultsHeaderProps) {
  const intl = useIntl();

  const localCounts = useValidationResultsCount(concepts);

  // Use preview stats when available, otherwise fall back to local computation
  const isPreview = totalCount !== undefined && domainBreakdown !== undefined;
  const conceptsFoundCount = isPreview ? totalCount : localCounts.conceptsFoundCount;
  const conceptsNotFoundCount = isPreview ? 0 : localCounts.conceptsNotFoundCount;
  const standardConceptsCount = isPreview ? totalCount : localCounts.standardConceptsCount;
  const nonStandardConceptsCount = isPreview ? 0 : localCounts.nonStandardConceptsCount;

  // For preview mode, use domainBreakdown keys; otherwise compute from concepts
  const domains = isPreview
    ? Object.keys(domainBreakdown)
    : concepts?.map((concept) => concept.domainId || '') || [];

  return (
    <Stack direction={'column'} gap={4} sx={{ mb: 2 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <ConceptGroupsDomainsInfo domains={domains} />

          <ConceptGroupConceptsCountInfo
            conceptsFoundCount={conceptsFoundCount}
            conceptsNotFoundCount={conceptsNotFoundCount}
            standardConceptsCount={standardConceptsCount}
            nonStandardConceptsCount={nonStandardConceptsCount}
          />
        </Box>
        {onExportAll && (
          <Icon
            id="download-validation-results-csv"
            sx={{
              cursor: exporting ? 'progress' : 'pointer',
              opacity: exporting ? 0.5 : 0.65,
              margin: 1,
              color: theme.palette.action.active,
            }}
            aria-label="download"
            icon="download"
            onClick={exporting ? undefined : onExportAll}
          />
        )}
      </Box>
    </Stack>
  );
}

export const ConceptGroupConceptsCountInfo = ({
  conceptsFoundCount,
  conceptsNotFoundCount,
  standardConceptsCount,
  nonStandardConceptsCount,
}: {
  conceptsFoundCount: number;
  conceptsNotFoundCount: number;
  standardConceptsCount: number;
  nonStandardConceptsCount: number;
}) => {
  const intl = useIntl();

  return (
    <>
      {/* Render concepts not found count, if there are any */}
      {conceptsNotFoundCount > 0 && (
        <InfoItem
          icon={<ErrorIcon color="error" sx={iconSx} />}
          label={intl.formatMessage({ id: 'conceptGroups.validationResults.conceptsNotFound' })}
          value={conceptsNotFoundCount}
        />
      )}

      {/* Render standard concepts count */}
      {standardConceptsCount !== undefined && nonStandardConceptsCount !== undefined && (
        <InfoItem
          icon={<CircleIcon color="primary" sx={iconSx} />}
          label={intl.formatMessage({ id: 'conceptGroups.validationResults.standardConcepts' })}
          value={`${standardConceptsCount}/${standardConceptsCount + nonStandardConceptsCount}`}
        />
      )}
    </>
  );
};

export const ConceptGroupsDomainsInfo = ({ domains }: { domains: string[] }) => {
  const intl = useIntl();

  const domainsSet = new Set(domains);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <MapsHomeWorkOutlinedIcon sx={{ mr: 1, height: '16px', width: '16px' }} />
      <Typography color={theme.palette.text.secondary} variant="body2" marginRight={1}>
        {intl.formatMessage({ id: 'conceptGroup.details.domains' })}
      </Typography>
      <Typography color={theme.palette.text.primary} variant="body2">
        {domainsSet.size > 0 ? (
          Array.from(domainsSet).join(', ')
        ) : (
          <Skeleton variant="text" height={20} width={100} />
        )}
      </Typography>
    </Box>
  );
};
